/**
 * ============================================================================
 * FILTER CONTROL BAR — inline-labelled pill controls over the filterQuery.
 * ============================================================================
 *
 * The filter SHELL (spec filter-control-shell), following the signed-off mock
 * (docs/filter-ux-mock.html, Option 5): one wrapping row of compact pills
 * ("Customer: All ▾" — Cloudscape inlineLabelText, no FormField label rows,
 * no surrounding container box), the "More filters (N)" trigger and
 * Reset-to-defaults inline at the row's end, and the remaining surface
 * properties in a full-width wrapping row that expands below. (Save Preset
 * was removed from the shell — presets are cut from scope.)
 *
 * PRIMARIES are the six dimension filters people slice by (Customer, Domain,
 * Locale, Channel, Sub Channel, DCR ID — config PRIMARY_FILTER_KEYS); the
 * WBR parity defaults (Report Period, Customer Type, Order Category, the DCR
 * flags) live behind More filters, with "More filters (N)" + the summary
 * count carrying the signal that they constrain.
 *
 * No Apply button: filtering is an instant client-side recompute, edits apply
 * LIVE (staging behind Apply is a QuickSight-ism that only adds latency).
 *
 * STATE stays the shared, persisted `filterQuery` (deep-dive spec D3): one
 * filter system app-wide, edits recompute locally, never an API call.
 */

import React, { useState } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import {
  FilterQuery,
  filterQueryChanged,
} from 'src/features/dashboard/dashboardSlice';
import {
  DEFAULT_FILTER_QUERY,
  FILTER_PROPERTIES,
  activeFilterCount,
} from 'src/config/filterConfig';
import { useFilterOptions } from 'src/components/filters/useFilterOptions';
import { FilterControl } from 'src/components/filters/controls/FilterControl';
import { DateRangeControl } from 'src/components/filters/controls/DateRangeControl';
import { constrainedKeys } from 'src/components/filters/controls/filterControlModel';

/** dcrCreatedDate renders as the dedicated From/To control, not a dropdown. */
const DATE_RANGE_KEY = 'dcrCreatedDate';

export interface FilterControlBarProps {
  /** The surface's property set (deep-dive vs summary — deep-dive spec R4.8). */
  properties: typeof FILTER_PROPERTIES;
  /** Pinned primary property keys (config: PRIMARY_FILTER_KEYS). */
  primaryKeys: string[];
  /**
   * The surface's VISIBLE week window. Kept on the contract for the pages
   * (population scoping) even though the bar no longer renders a counter.
   */
  weeks?: string[];
  /**
   * Per-property VIRTUAL default selections (see FilterControl.defaultSelected):
   * rendered checked while the property has no tokens, never persisted.
   */
  defaultSelections?: Record<string, string[]>;
}

export const FilterControlBar: React.FC<FilterControlBarProps> = ({
  properties,
  primaryKeys,
  defaultSelections,
}) => {
  const dispatch = useAppDispatch();
  const query = useAppSelector((state) => state.dashboard.filterQuery);
  const [expanded, setExpanded] = useState(false);
  const options = useFilterOptions();

  const onQueryChange = (next: FilterQuery) =>
    dispatch(filterQueryChanged(next));

  const valuesFor = (key: string): string[] =>
    options.filter((o) => o.propertyKey === key).map((o) => o.value);

  const primaries = properties.filter(
    (p) => primaryKeys.indexOf(p.key) !== -1 && p.key !== DATE_RANGE_KEY,
  );
  const secondaries = properties.filter(
    (p) => primaryKeys.indexOf(p.key) === -1 && p.key !== DATE_RANGE_KEY,
  );
  const hasDateRange = properties.some((p) => p.key === DATE_RANGE_KEY);

  // "More filters (N)": N = how many NON-primary properties currently
  // constrain — with the parity defaults living in the overflow, this is the
  // visible signal that hidden filters are shaping the numbers.
  const constrained = constrainedKeys(query);
  const moreCount =
    secondaries.filter((p) => constrained.has(p.key)).length +
    (hasDateRange && constrained.has(DATE_RANGE_KEY) ? 1 : 0);
  const differsFromDefault = activeFilterCount(query);

  return (
    <div>
      {/* One wrapping pill row: primaries + the overflow toggle + actions
          inline at the end (mock Option 5 — no container box around it). */}
      <div className="filter-shell__primaries">
        {primaries.map((property) => (
          <div className="filter-shell__pill" key={property.key}>
            <FilterControl
              propertyKey={property.key}
              label={property.propertyLabel}
              values={valuesFor(property.key)}
              query={query}
              onQueryChange={onQueryChange}
              defaultSelected={defaultSelections?.[property.key]}
            />
          </div>
        ))}
        {(secondaries.length > 0 || hasDateRange) && (
          <Button
            variant="normal"
            iconName="filter"
            onClick={() => setExpanded((open) => !open)}
          >
            {`${expanded ? 'Hide filters' : 'More filters'}${
              moreCount > 0 ? ` (${moreCount})` : ''
            }`}
          </Button>
        )}
        <Button
          variant="inline-link"
          disabled={differsFromDefault === 0}
          onClick={() => onQueryChange(DEFAULT_FILTER_QUERY as FilterQuery)}
        >
          Reset to defaults
        </Button>
        {differsFromDefault > 0 && (
          <Box color="text-body-secondary" fontSize="body-s">
            {differsFromDefault === 1
              ? '1 filter differs from WBR default'
              : `${differsFromDefault} filters differ from WBR default`}
          </Box>
        )}
      </div>

      {/* Inline overflow — FULL WIDTH wrapping row below the primaries, so an
          open dropdown never covers its neighbours. (A right-side drawer was
          tried and REJECTED in live review.) Edits apply LIVE — no Apply. */}
      {expanded && (
        <div className="filter-shell__more">
          {secondaries.map((property) => (
            <div className="filter-shell__pill" key={property.key}>
              <FilterControl
                propertyKey={property.key}
                label={property.propertyLabel}
                values={valuesFor(property.key)}
                query={query}
                onQueryChange={onQueryChange}
                defaultSelected={defaultSelections?.[property.key]}
              />
            </div>
          ))}
          {hasDateRange && (
            <DateRangeControl query={query} onQueryChange={onQueryChange} />
          )}
        </div>
      )}
    </div>
  );
};
